import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef } from "@angular/core";
import { NotificationsService, Notification, NotificationSummary, ToastNotification } from "../notifications.service";
import { UserManagementService } from "src/app/user-management/user-management.service";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";

// Filter tabs for notification categories
type NotificationFilter = 'all' | 'unread' | 'processing' | 'errors';

@Component({
  selector: "app-notification-list",
  templateUrl: "./notification-list.component.html",
  styleUrls: ["./notification-list.component.css"],
  standalone: false,
})
export class NotificationListComponent implements OnInit, OnDestroy {
  @Output() notificationClicked = new EventEmitter<void>();
  notifications: Notification[] = [];
  filteredNotifications: Notification[] = [];
  toasts: ToastNotification[] = [];
  summary: NotificationSummary = { total: 0, unread: 0, processing: 0, errors: 0, recentCount: 0 };
  user: any;
  loading = false;
  selectedType: string = "all";
  selectedFilter: NotificationFilter = "all";
  unreadCount = 0;
  isDarkMode = false;
  showSummary = true;
  private subscriptions: Subscription[] = [];

  // Category filter tabs
  filterTabs: { value: NotificationFilter; label: string; icon: string }[] = [
    { value: "all", label: "All", icon: "fa-bell" },
    { value: "unread", label: "Unread", icon: "fa-envelope" },
    { value: "processing", label: "Processing", icon: "fa-spinner" },
    { value: "errors", label: "Errors", icon: "fa-exclamation-circle" },
  ];

  notificationTypes = [
    { value: "all", label: "All Types" },
    { value: "alert", label: "Alerts" },
    { value: "upload", label: "Uploads" },
    { value: "system", label: "System" },
    { value: "report", label: "Reports" },
    { value: "user", label: "User" },
    { value: "general", label: "General" },
  ];

  constructor(
    private notificationService: NotificationsService,
    private userService: UserManagementService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.user = this.userService.getUserDetail();
    this.loadNotifications();
    
    // Load theme preference from localStorage
    const savedTheme = localStorage.getItem('notification_theme');
    this.isDarkMode = savedTheme === 'dark';
    
    // Subscribe to notification updates
    const notificationSub = this.notificationService.notifications$.subscribe(
      (notifications) => {
        this.notifications = notifications;
        this.filterNotifications();
      }
    );
    this.subscriptions.push(notificationSub);

    const unreadSub = this.notificationService.unreadCount$.subscribe(
      (count) => {
        this.unreadCount = count;
        // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();
      }
    );
    this.subscriptions.push(unreadSub);

    // Subscribe to summary updates
    const summarySub = this.notificationService.summary$.subscribe(
      (summary) => {
        this.summary = summary;
        this.cdr.detectChanges();
      }
    );
    this.subscriptions.push(summarySub);

    // Subscribe to toasts
    const toastSub = this.notificationService.toasts$.subscribe(
      (toasts) => {
        this.toasts = toasts;
        this.cdr.detectChanges();
      }
    );
    this.subscriptions.push(toastSub);
  }

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('notification_theme', this.isDarkMode ? 'dark' : 'light');
  }

  toggleSummary(): void {
    this.showSummary = !this.showSummary;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private getCurrentUserId(): string | number | null {
    return this.user?.user_id || this.user?.id || null;
  }

  loadNotifications(): void {
    this.loading = true;
    const userId = this.getCurrentUserId();
    if (userId) {
      this.notificationService.getUserNotifications(userId).subscribe(
        (response: any) => {
          this.loading = false;
          if (response && response.data) {
            const formattedNotifications = this.formatNotifications(response.data);
            this.notifications = formattedNotifications;
            // Update the service with loaded notifications
            this.notificationService.setNotifications(formattedNotifications);
            this.filterNotifications();
          } else {
            // No notifications from API
            this.notifications = [];
            this.filterNotifications();
          }
        },
        (error) => {
          this.loading = false;
          console.error("Error loading notifications:", error);
          // No mock data - just show empty state
          this.notifications = [];
          this.filterNotifications();
        }
      );
    } else {
      this.loading = false;
      // No user ID - show empty state
      this.notifications = [];
      this.filterNotifications();
    }
  }

  formatNotifications(data: any[]): Notification[] {
    return data.map((item) => ({
      id: item.id || item.notification_id,
      title: item.title || item.notification_title,
      message: item.message || item.notification_message,
      type: item.type || item.notification_type || "general",
      severity: item.severity || item.notification_severity || "info",
      timestamp: item.timestamp || item.created_at || new Date(),
      read: item.read || item.is_read || false,
      actionUrl: item.action_url || item.actionUrl,
      metadata: item.metadata || item,
      priority: item.priority || 'medium',
      shortMessage: item.shortMessage || this.createShortMessage(item.message || item.notification_message || ''),
      isProcessing: item.isProcessing || item.severity === 'updating',
    }));
  }

  private createShortMessage(message: string): string {
    let clean = message.replace(/<[^>]*>/g, '');
    if (clean.length > 60) {
      clean = clean.substring(0, 57) + '...';
    }
    return clean;
  }

  filterNotifications(): void {
    let filtered = [...this.notifications];

    // Apply category filter first
    switch (this.selectedFilter) {
      case 'unread':
        filtered = filtered.filter(n => !n.read);
        break;
      case 'processing':
        filtered = filtered.filter(n => n.isProcessing || n.severity === 'updating');
        break;
      case 'errors':
        filtered = filtered.filter(n => n.severity === 'error');
        break;
    }

    // Then apply type filter
    if (this.selectedType !== "all") {
      filtered = filtered.filter(n => n.type === this.selectedType);
    }

    // Sort by priority then timestamp (newest first)
    filtered.sort((a, b) => {
      // Priority order: critical > high > medium > low
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.priority || 'medium'];
      const bPriority = priorityOrder[b.priority || 'medium'];
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });

    this.filteredNotifications = filtered;
  }

  onFilterTabChange(filter: NotificationFilter): void {
    this.selectedFilter = filter;
    this.filterNotifications();
  }

  onTypeFilterChange(): void {
    this.filterNotifications();
  }

  // ── Phone type filter ────────────────────────────────────────────────
  // The native <select> picker is drawn by the OS and cannot be styled to
  // match the rest of this UI, so phones get a sheet instead. The select
  // itself is untouched and still serves every width above 768px.

  showTypeSheet = false;

  get selectedTypeLabel(): string {
    return (
      this.notificationTypes.find((t) => t.value === this.selectedType)
        ?.label || "All Types"
    );
  }

  openTypeSheet(): void {
    this.showTypeSheet = true;
  }

  closeTypeSheet(): void {
    this.showTypeSheet = false;
  }

  chooseType(value: string): void {
    this.selectedType = value;
    this.showTypeSheet = false;
    this.onTypeFilterChange();
  }

  markAsRead(notification: Notification): void {
    if (!notification.read) {
      // Mark locally first for immediate UI feedback
      notification.read = true;
      
      if (notification.id) {
        // Try API call (non-blocking)
        this.notificationService.markAsRead(notification.id).subscribe(
          () => {
            console.log('Notification marked as read on server');
          },
          (error) => {
            console.error("Error marking notification as read on server:", error);
            // Already marked locally, so just log the error
          }
        );
        // Update in service
        this.notificationService.markNotificationAsRead(notification.id);
      } else {
        // Handle notification without ID - update by reference in service
        this.notificationService.markNotificationAsReadByReference(notification);
      }
      
      this.filterNotifications();
      this.cdr.detectChanges();
    }
  }

  markAllAsRead(): void {
    const userId = this.getCurrentUserId();
    if (userId) {
      this.notificationService.markAllAsRead(userId).subscribe(
        () => {
          this.notificationService.markAllNotificationsAsRead();
          this.notifications.forEach((n) => (n.read = true));
          this.filterNotifications();
        },
        (error) => {
          console.error("Error marking all as read:", error);
          // Update locally even if API call fails
          this.notificationService.markAllNotificationsAsRead();
          this.notifications.forEach((n) => (n.read = true));
          this.filterNotifications();
        }
      );
    } else {
      // If no user, just update locally
      this.notificationService.markAllNotificationsAsRead();
      this.notifications.forEach((n) => (n.read = true));
      this.filterNotifications();
    }
  }

  deleteNotification(notification: Notification): void {
    // Store the notification ID for comparison
    const notificationId = notification.id;
    
    if (notificationId !== undefined && notificationId !== null) {
      // Try to delete from backend first
      this.notificationService.deleteNotification(notificationId).subscribe(
        () => {
          // Remove from local store (this will trigger the observable update)
          this.notificationService.removeNotification(notificationId);
        },
        (error) => {
          console.error("Error deleting notification:", error);
          // Remove locally even if API call fails
          this.notificationService.removeNotification(notificationId);
        }
      );
    } else {
      // If no ID, remove by object reference
      this.notificationService.removeNotificationByReference(notification);
    }
  }

  clearAllNotifications(): void {
    if (this.notifications.length === 0) {
      return;
    }

    const userId = this.getCurrentUserId();
    if (userId) {
      // Try to delete all from backend first
      this.notificationService.deleteAllNotifications(userId).subscribe(
        () => {
          // Clear local store
          this.notificationService.clearAllNotifications();
        },
        (error) => {
          console.error("Error clearing all notifications:", error);
          // Clear locally even if API call fails
          this.notificationService.clearAllNotifications();
        }
      );
    } else {
      // If no user, just clear locally
      this.notificationService.clearAllNotifications();
    }
  }

  dismissToast(toastId: string): void {
    this.notificationService.dismissToast(toastId);
  }

  navigateToAction(notification: Notification): void {
    if (notification.actionUrl) {
      this.markAsRead(notification);
      this.router.navigate([notification.actionUrl]);
      // Emit event to close panel if in panel mode
      this.notificationClicked.emit();
    }
  }

  getPriorityClass(priority: string | undefined): string {
    const classes: { [key: string]: string } = {
      critical: "priority-critical",
      high: "priority-high",
      medium: "priority-medium",
      low: "priority-low",
    };
    return classes[priority || 'medium'] || "priority-medium";
  }

  getSeverityClass(severity: string): string {
    const classes: { [key: string]: string } = {
      info: "badge-info",
      success: "badge-success",
      warning: "badge-warning",
      error: "badge-danger",
      'site setting': "badge-warning",
      updating: "badge-updating",
    };
    return classes[severity] || "badge-secondary";
  }

  /**
   * Get the display label for severity based on operation type
   * Shows "CREATING" for create operations, "REFRESHING" for refresh, etc.
   */
  getSeverityLabel(notification: Notification): string {
    // If severity is "updating", check the operation type to show appropriate label
    if (notification.severity === 'updating' && notification.metadata?.operationType) {
      const opType = notification.metadata.operationType;
      if (opType === 'create') {
        return 'creating';
      } else if (opType === 'update') {
        return 'updating';
      } else if (opType === 'refresh') {
        return 'refreshing';
      }
    }
    // For other severities, return as-is
    return notification.severity;
  }

  getSeverityIcon(severity: string): string {
    const icons: { [key: string]: string } = {
      info: "fa-info-circle",
      success: "fa-check-circle",
      warning: "fa-exclamation-triangle",
      error: "fa-times-circle",
      'site setting': "fa-cog",
      updating: "fa-spinner fa-spin",
    };
    return icons[severity] || "fa-bell";
  }

  getTypeIcon(type: string): string {
    const icons: { [key: string]: string } = {
      alert: "fa-exclamation-triangle",
      upload: "fa-upload",
      system: "fa-cog",
      report: "fa-file-alt",
      user: "fa-user",
      general: "fa-bell",
    };
    return icons[type] || "fa-bell";
  }

  getToastIcon(type: string): string {
    const icons: { [key: string]: string } = {
      success: "fa-check-circle",
      error: "fa-times-circle",
      warning: "fa-exclamation-triangle",
      info: "fa-info-circle",
    };
    return icons[type] || "fa-bell";
  }

  formatTimestamp(timestamp: Date | string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  getFilterCount(filter: NotificationFilter): number {
    switch (filter) {
      case 'all': return this.summary.total;
      case 'unread': return this.summary.unread;
      case 'processing': return this.summary.processing;
      case 'errors': return this.summary.errors;
      default: return 0;
    }
  }

  trackByNotificationId(index: number, notification: Notification): string | number {
    return notification.id || index;
  }

  // Track expanded multi-page notifications
  private expandedMultiPageNotifications = new Set<string | number>();

  /**
   * Toggle multi-page dropdown expansion
   */
  toggleMultiPageDropdown(notification: Notification): void {
    const id = notification.id;
    if (!id) return;
    
    if (this.expandedMultiPageNotifications.has(id)) {
      this.expandedMultiPageNotifications.delete(id);
    } else {
      this.expandedMultiPageNotifications.add(id);
    }
  }

  /**
   * Check if multi-page dropdown is expanded
   */
  isMultiPageExpanded(notification: Notification): boolean {
    return notification.id ? this.expandedMultiPageNotifications.has(notification.id) : false;
  }

  /**
   * Get count of completed pages in multi-page notification
   */
  getCompletedPagesCount(notification: Notification): number {
    const dashboards = notification.metadata?.dashboards;
    if (!dashboards || !Array.isArray(dashboards)) return 0;
    return dashboards.filter((d: any) => d.status === 'completed').length;
  }

  // Track expanded deleted reports notifications
  private expandedDeletedNotifications = new Set<string | number>();

  /**
   * Toggle deleted reports dropdown expansion
   */
  toggleDeletedDropdown(notification: Notification): void {
    const id = notification.id;
    if (!id) return;
    if (this.expandedDeletedNotifications.has(id)) {
      this.expandedDeletedNotifications.delete(id);
    } else {
      this.expandedDeletedNotifications.add(id);
    }
  }

  /**
   * Check if deleted reports dropdown is expanded
   */
  isDeletedExpanded(notification: Notification): boolean {
    return notification.id ? this.expandedDeletedNotifications.has(notification.id) : false;
  }

  /**
   * Get deleted report names count
   */
  getDeletedReportsCount(notification: Notification): number {
    const names = notification.metadata?.deletedReportNames;
    if (!names || !Array.isArray(names)) return 0;
    return names.length;
  }

  // Track expanded steps notifications (for site save progress)
  private expandedStepsNotifications = new Set<string | number>();

  /**
   * Toggle steps dropdown expansion
   */
  toggleStepsDropdown(notification: Notification): void {
    const id = notification.id;
    if (!id) return;
    
    if (this.expandedStepsNotifications.has(id)) {
      this.expandedStepsNotifications.delete(id);
    } else {
      this.expandedStepsNotifications.add(id);
    }
  }

  /**
   * Check if steps dropdown is expanded
   */
  isStepsExpanded(notification: Notification): boolean {
    return notification.id ? this.expandedStepsNotifications.has(notification.id) : false;
  }

  /**
   * Get count of completed steps in notification
   */
  getCompletedStepsCount(notification: Notification): number {
    const steps = notification.metadata?.steps;
    if (!steps || !Array.isArray(steps)) return 0;
    return steps.filter((s: any) => s.status === 'completed').length;
  }
}
